import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/lib/constants";

const stack = [
  {
    name: "SQL",
    role: "Consultas, modelado y ETL",
    desc: "Acceso a tu ERP y bases de datos para extraer, cruzar y transformar información a escala."
  },
  {
    name: "Power BI",
    role: "Dashboards y reporting",
    desc: "Cuadros de mando ejecutivos, modelo semántico y publicación con refresco automático."
  },
  {
    name: "DAX",
    role: "Cálculos de negocio",
    desc: "Medidas avanzadas: margen real, rotación, días de cobertura, comparativas YoY."
  },
  {
    name: "Power Query",
    role: "Transformación de datos",
    desc: "Limpieza, normalización y combinación de fuentes antes de cargar al modelo."
  },
  {
    name: "Python",
    role: "Automatización y modelos",
    desc: "Scripts de consolidación, forecasting (Prophet, scikit-learn) y análisis avanzado."
  },
  {
    name: "PostgreSQL",
    role: "Base de datos",
    desc: "Almacén relacional para datos integrados desde múltiples fuentes operativas."
  },
  {
    name: "MySQL",
    role: "Base de datos",
    desc: "Conexión y extracción desde sistemas legacy o ERPs basados en MySQL."
  },
  {
    name: "APIs REST",
    role: "Integración entre sistemas",
    desc: "Consumo y exposición de datos entre ERPs, CRMs, ecommerce y herramientas SaaS."
  },
  {
    name: "ETL",
    role: "Pipelines de datos",
    desc: "Orquestación del flujo: extracción → transformación → carga al modelo analítico."
  },
  {
    name: "Excel avanzado",
    role: "Análisis ágil",
    desc: "Tablas dinámicas, fórmulas matriciales y conexión a modelos para análisis rápidos."
  }
];

export const TecnologiasSection = () => {
  return (
    <section className="relative section-padding bg-slate-900/50 overflow-hidden" data-testid="tecnologias-section">
      <div className="spotlight-1 -top-20 right-0" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="text-indigo-400 font-medium text-sm uppercase tracking-wider font-mono">
              Stack tecnológico
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4">
              Las herramientas que uso{" "}
              <span className="text-gradient">y para qué sirven en negocio</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mt-4 text-base">
              No herramientas por usarlas: cada pieza del stack resuelve una parte concreta del pipeline.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-10">
            {stack.map((tech, i) => (
              <motion.div key={i} variants={fadeInUp}>
                <div className="h-full bg-slate-900 border border-slate-800 hover:border-indigo-500/40 rounded-xl p-5 transition-colors" data-testid={`stack-${tech.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="font-mono text-white font-bold tracking-tight">{tech.name}</h3>
                  </div>
                  <p className="text-indigo-400 text-xs uppercase tracking-wider mb-2 font-medium">{tech.role}</p>
                  <p className="text-slate-400 text-sm leading-relaxed">{tech.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeInUp} className="max-w-3xl mx-auto">
            <div className="text-slate-400 text-base leading-relaxed space-y-4">
              <p>
                Trabajo con el stack que tu empresa ya tiene. Si usas Power BI, integro contra él.
                Si tu ERP expone una API o una base SQL Server, conecto directamente. Si la fuente
                principal son hojas de Excel desordenadas, las normalizo con Power Query y Python
                antes de modelar nada.
              </p>
              <p>
                Para casos que requieren más profundidad técnica, puedes ver mi enfoque como{" "}
                <Link to="/analista-datos-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">analista de datos en Murcia</Link>{" "}
                o el servicio completo de{" "}
                <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">business intelligence Murcia</Link>.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
